import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

// UTILS, CONSTS, CONFIG
import {
  EFulfillmentOrderAction,
  EFulfillmentOrderStatus,
} from '@app/core/_constant/fulfillment-orders.constants';
import { ShipOrdersComponent } from '@app/modules/shipping/ship-orders/ship-orders.component';

import { DialogConfig, FeatureFlagUtilService } from '../../../shared';
import { FulfillmentOrderApiService } from '../../../shared/services/api/fulfillment-order-api.service';
import { OrderPostedApiService } from '../../../shared/services/api/order/order-posted-api.service';
import { trackById } from '../../../shared/util';

// INTERFACES

// SERVICES
import { UiConfirmationDialogComponent } from '../../../ui';
import { ESalesOrderStatus } from '../../interfaces';
import { OrderFulfillmentStatusUpdateComponent } from '../order-fulfillment-status-update/order-fulfillment-status-update.component';
import { OrderShipFormV2Component } from '../order-ship-form-v2/order-ship-form-v2.component';

@Component({
  selector: 'lum-order-fulfillment',
  templateUrl: './order-fulfillment.component.html',
  styleUrls: ['./order-fulfillment.component.scss'],
})
export class OrderFulfillmentComponent implements OnInit, OnDestroy {
  @Output()
  closeModalEvent = new EventEmitter<boolean>();

  fulfillmentOrderId :number | null = null;
  salesOrderId :number | null = null;

  salesOrder :any = {}; // TODO add salesOrder interface
  fulfillmentOrder :any = {}; // TODO add fulfillmentOrder interface

  relations :string[] = [
    'shipmentItems',
    'shipmentItems.lot',
    'warehouseGroup:id,label',
    'destinationOms:id,label',
    'destinationOmsStore:id,storeName',
  ];

  salesOrderRelations :string = [
    'orderItems.orderedUom:id,name',
    'channel.pushOrderIntegrationMapping.appIntegrationAccount',
  ].join('|');

  trackById = trackById;

  loading :boolean = false;
  private onDestroy$ = new Subject();
  isInModal :boolean = false;
  currentActiveTab :number = 0;

  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private fulfillmentOrderApiService :FulfillmentOrderApiService,
    private snackBar :MatSnackBar,
    private route :ActivatedRoute,
    private router :Router,
    private fulfillmentOrdersApiService :FulfillmentOrderApiService,
    private featureFlagUtilService :FeatureFlagUtilService,
    private modal :NgbModal,
    private orderPostedApiService :OrderPostedApiService,
  ) {}

  ngOnInit() :void {
    this.getRouterParams();
    this.getFulfillmentOrder();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  closeModal() :void {
    this.closeModalEvent.emit(true);
  }

  updateFulfillmentOrder() :void {
    const salesOrderData :any[] = [];
    salesOrderData.push(this.salesOrder);

    if (this.ftPartialFulfillment) {
      this.dialog
        .open(OrderShipFormV2Component, {
          data: {
            title: 'Fulfill Orders',
            salesOrders: salesOrderData,
            ftEdiIntegrationEnabled: this.ftEdiIntegration,
            fulfillmentOrder: this.fulfillmentOrder,
            isCreateFulfillmentOrder: false,
          },
          width: DialogConfig.sizes['xx-large'],
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response :boolean) => {
          this.getFulfillmentOrder();
        });

      return;
    }
    const modalRef = this.modal.open(ShipOrdersComponent, {
      size: 'lg',
      windowClass: 'ship-orders-modal',
    });
    modalRef.componentInstance.salesOrders = salesOrderData;
    modalRef.result.then(
      (hasUpdates :boolean) => {
        if (hasUpdates) {
          this.getFulfillmentOrder();
        }
      },
      (_) => {},
    );
  }

  updateStatusFulfillmentOrder() :void {
    this.dialog
      .open(OrderFulfillmentStatusUpdateComponent, {
        data: {
          title: 'Update Order Status',
          orderStatusLookups: Object.values(EFulfillmentOrderStatus),
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :any) => {
        const { orderStatus } = result;

        if (orderStatus) {
          this.fulfillmentOrdersApiService
            .updateStatus(this.fulfillmentOrderId, {
              orderStatus: orderStatus,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                this.snackBar.open('Record status updated', 'Close', {
                  duration: 3000,
                });
                this.getFulfillmentOrder();
              },
              error: (e) => {
                this.snackBar.open(
                  'Cannot update the status of this record.',
                  'Close',
                  {
                    duration: 3000,
                  },
                );
              },
            });
        }
      });
  }

  deleteFulfillmentOrder() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: 'This will delete the fulfillment order. Are you sure?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.fulfillmentOrdersApiService
            .delete(this.fulfillmentOrderId)
            .subscribe({
              next: () => {
                this.snackBar.open('Record deleted', 'Close', {
                  duration: 3000,
                });
                this.router.navigate([
                  '/shipping/posted-orders/',
                  this.salesOrderId,
                ]);
              },
              error: (e) => {
                this.snackBar.open('Cannot delete this record.', 'Close', {
                  duration: 3000,
                });
              },
            });
        }
      });
  }

  private getRouterParams() :void {
    this.route.params
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params :Params) => {
        const id = params['id'];
        if (id) {
          this.fulfillmentOrderId = id;
        } else {
          this.isInModal = true;
        }
      });
  }
  private getSalesOrder() :void {
    this.loading = true;

    this.orderPostedApiService
      .getById({
        id: this.fulfillmentOrder.salesOrderId ?? 0,
        params: {
          relations: this.salesOrderRelations,
          relationsWithColumns: 'true',
          excludeIgnoredOrderItems: 'true',
        },
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap({
          next: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        }),
      )
      .subscribe({
        next: (salesOrder :any) => {
          if (salesOrder) {
            this.salesOrder = salesOrder;
          }
          this.cd.detectChanges();
        },
      });
  }

  private getFulfillmentOrder() :void {
    this.loading = true;

    this.fulfillmentOrderApiService
      .getById({
        id: this.fulfillmentOrderId ?? 0,
        params: {
          relations: this.relations.join('|'),
          relationsWithColumns: 'true',
        },
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap({
          next: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        }),
      )
      .subscribe({
        next: (fulfillmentOrder :any) => {
          if (fulfillmentOrder) {
            this.fulfillmentOrder = fulfillmentOrder;
            this.getSalesOrder();
          }
          this.cd.detectChanges();
        },
      });
  }

  get ftPartialFulfillment() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2840_partial_fulfillment',
    );
  }

  get ftEdiIntegration() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-2272_edi_integration',
    );
  }
}
